import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { ButtonStyled } from "@nike/nike-design-system-components";
import PgTypeBadge from "./PgTypeBadge";
import SortingRuleChip from "./SortingRuleChip";
import ShipToCell from "./ShipToCell";
import {
  PgFilterDropdown,
  PgFilterTextField,
  PgFilterActions,
} from "./PgFilterField";
import { pgDefaultColDef, pgGridOptions, pgRowCheckboxColDef } from "./pgGridConfig";
import {
  DEFAULT_PAGE_SIZE,
  PgTablePagination,
  slicePage,
} from "./pgPagination";
import { PG_FILTER_ALL, isFilterAll } from "./pgFilterConstants";
import {
  pgGridRowClickToggleProps,
  useLimitedGridSelection,
} from "./pgGridSelection";
import { usePgGridColumnFit } from "./pgGridResize";

const PG_TYPE_OPTIONS = [
  { label: "All Types", value: PG_FILTER_ALL },
  { label: "Customer", value: "Customer" },
  { label: "Nike", value: "Nike" },
];

function UnassignedPalletGroupSection({
  rowData,
  onSelectionChanged,
  selectedCount,
  onOpenIlpns,
  maxSelection,
  selectionResetKey = 0,
}) {
  const [palletGroup, setPalletGroup] = useState(PG_FILTER_ALL);
  const [asnId, setAsnId] = useState("");
  const [pgType, setPgType] = useState(PG_FILTER_ALL);
  const [inboundId, setInboundId] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    palletGroup: "",
    asnId: "",
    pgType: "",
    inboundId: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const gridApiRef = useRef(null);

  useEffect(() => {
    if (selectionResetKey > 0) {
      gridApiRef.current?.deselectAll();
    }
  }, [selectionResetKey]);

  const pgOptions = useMemo(
    () => [
      { label: "All Pallet Groups", value: PG_FILTER_ALL },
      ...rowData.map((r) => ({ label: r.palletGroup, value: r.palletGroup })),
    ],
    [rowData],
  );

  const filteredRows = useMemo(() => {
    return rowData.filter((row) => {
      if (appliedFilters.palletGroup && row.palletGroup !== appliedFilters.palletGroup) {
        return false;
      }
      if (
        appliedFilters.asnId &&
        row.asn.toLowerCase() !== appliedFilters.asnId.toLowerCase()
      ) {
        return false;
      }
      if (appliedFilters.pgType && row.pgType !== appliedFilters.pgType) {
        return false;
      }
      if (
        appliedFilters.inboundId &&
        row.inboundId.toLowerCase() !== appliedFilters.inboundId.toLowerCase()
      ) {
        return false;
      }
      return true;
    });
  }, [rowData, appliedFilters]);

  const { pageRows, totalPages, page: safePage, totalItems } = useMemo(
    () => slicePage(filteredRows, page, pageSize),
    [filteredRows, page, pageSize],
  );

  useEffect(() => {
    setPage(safePage);
  }, [safePage]);

  const columnDefs = useMemo(
    () => [
      { ...pgRowCheckboxColDef },
      {
        headerName: "Pallet Group",
        field: "palletGroup",
        minWidth: 112,
        flex: 1.15,
        cellClass: "pg-cell-strong",
      },
      {
        headerName: "Type",
        field: "type",
        minWidth: 72,
        flex: 0.55,
        cellRenderer: (p) => <PgTypeBadge value={p.value} />,
      },
      {
        headerName: "Inbound ID",
        field: "inboundId",
        minWidth: 88,
        flex: 0.65,
      },
      { headerName: "ASN", field: "asn", minWidth: 84, flex: 0.6 },
      {
        headerName: "Ship-to",
        field: "shipToCode",
        minWidth: 100,
        flex: 1,
        cellRenderer: (p) => <ShipToCell data={p.data} />,
        cellClass: "pg-cell-ship-to",
        headerClass: "pg-grid-header-cell pg-grid-header-cell--ship-to",
      },
      { headerName: "PO", field: "po", minWidth: 88, flex: 0.6 },
      {
        headerName: "Total ILPNs",
        field: "totalILPNs",
        minWidth: 88,
        flex: 0.65,
        cellRenderer: (p) => (
          <button
            type="button"
            className="pg-link-cell"
            onClick={(e) => {
              e.stopPropagation();
              onOpenIlpns(p.data);
            }}
          >
            {p.value} ILPNs
          </button>
        ),
      },
      {
        headerName: "Sorting Rule",
        field: "sortingRule",
        minWidth: 96,
        flex: 0.75,
        cellRenderer: (p) => <SortingRuleChip value={p.value} />,
      },
    ],
    [onOpenIlpns],
  );

  const onGridSelectionChanged = useLimitedGridSelection(
    onSelectionChanged,
    maxSelection,
    (row) => row.id,
  );

  const gridColumnFitProps = usePgGridColumnFit(pageRows);

  const applyFilters = () => {
    setAppliedFilters({
      palletGroup: isFilterAll(palletGroup) ? "" : palletGroup,
      asnId: asnId.trim(),
      pgType: isFilterAll(pgType) ? "" : pgType,
      inboundId: inboundId.trim(),
    });
    setPage(1);
  };

  const resetFilters = () => {
    setPalletGroup(PG_FILTER_ALL);
    setAsnId("");
    setPgType(PG_FILTER_ALL);
    setInboundId("");
    setAppliedFilters({
      palletGroup: "",
      asnId: "",
      pgType: "",
      inboundId: "",
    });
    setPage(1);
  };

  return (
    <>
      <h2 className="pg-panel-title">Unassigned Pallet Groups</h2>
      <div className="pg-filters">
        <div className="pg-filter-grid">
          <PgFilterDropdown
            name="palletGroup"
            label="Pallet Group"
            options={pgOptions}
            value={palletGroup}
            onChange={(e) => setPalletGroup(e.target.value)}
            ariaLive="polite"
          />
          <PgFilterTextField
            name="asnId"
            label="ASN ID"
            placeholder="Exact ASN — e.g. ASN-2609-A"
            value={asnId}
            onChange={(e) => setAsnId(e.target.value)}
            ariaLive="polite"
          />
          <PgFilterDropdown
            name="pgType"
            label="PG Type"
            options={PG_TYPE_OPTIONS}
            value={pgType}
            onChange={(e) => setPgType(e.target.value)}
            ariaLive="polite"
          />
          <PgFilterTextField
            label="Inbound ID"
            placeholder="Exact Inbound ID — e.g. IBD0012845"
            value={inboundId}
            onChange={(e) => setInboundId(e.target.value)}
            ariaLive="polite"
          />
          <PgFilterActions>
            <ButtonStyled
              appearance="primary"
              type="button"
              className="pg-btn-apply"
              onClick={applyFilters}
            >
              Apply
            </ButtonStyled>
            <ButtonStyled
              appearance="primary"
              type="button"
              className="pg-btn-reset"
              onClick={resetFilters}
            >
              Reset
            </ButtonStyled>
          </PgFilterActions>
        </div>
      </div>
      <div className="pg-table-wrap">
        <div className="pg-grid-wrap ag-theme-alpine ag-theme-pg pg-grid--unassigned">
          <AgGridReact
            rowData={pageRows}
            columnDefs={columnDefs}
            defaultColDef={pgDefaultColDef}
            {...pgGridOptions}
            {...gridColumnFitProps}
            rowSelection="multiple"
            {...pgGridRowClickToggleProps}
            suppressHeaderFocus={true}
            onSelectionChanged={onGridSelectionChanged}
            onGridReady={(params) => {
              gridApiRef.current = params.api;
            }}
            getRowId={(p) => p.data.id}
            rowClassRules={{
              "pg-row-selected": (p) => p.node.isSelected(),
              "pg-row-normal": () => true,
            }}
            overlayNoRowsTemplate='<span class="pg-grid-empty">No unassigned pallet groups match filters</span>'
          />
        </div>
      </div>
      <footer className="pg-panel-footer">
        <span className="pg-selection-hint">{selectedCount} selected</span>
        <PgTablePagination
          page={safePage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          onPageChange={setPage}
        />
      </footer>
    </>
  );
}

export default UnassignedPalletGroupSection;
